import { useState } from "react";
import { DateTime } from "luxon";
import { useQuery } from "@tanstack/react-query";
import { History, RotateCcw, X } from "lucide-react";
import type { AirportConfig, Disruption } from "@yb/core";
import { api } from "../lib/api";

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl border border-line bg-panel shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center border-b border-line px-4 py-3">
          <div className="flex-1 text-[15px] font-bold">{title}</div>
          <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-panel-2" aria-label="Close">
            <X className="size-4" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export function CloseStandDialog({
  config,
  date,
  now,
  standId,
  onCancel,
  onSubmit,
}: {
  config: AirportConfig;
  date: string;
  now: number;
  standId: string;
  onCancel: () => void;
  /** The disruption, and the day it is on - which need not be the day on the board. */
  onSubmit: (d: Disruption, day: string) => void;
}) {
  const tz = config.timezone;
  const start = DateTime.fromMillis(Math.max(now, DateTime.fromISO(date, { zone: tz }).set({ hour: 6 }).toMillis()), { zone: tz });
  const rounded = start.set({ minute: Math.ceil(start.minute / 15) * 15, second: 0, millisecond: 0 });
  const [stand, setStand] = useState(standId);
  // Any range, across any days: a stand can be out for an afternoon or a fortnight.
  const [from, setFrom] = useState(rounded.toFormat("yyyy-MM-dd'T'HH:mm"));
  const [to, setTo] = useState(rounded.plus({ hours: 3 }).toFormat("yyyy-MM-dd'T'HH:mm"));
  const [reason, setReason] = useState("Fuel spill");
  const toMs = (v: string) => DateTime.fromISO(v, { zone: tz }).toMillis();
  /** The day the preview is drawn on: the first one the closure touches. */
  const day = from.slice(0, 10);
  const valid = !Number.isNaN(toMs(from)) && toMs(to) > toMs(from) && reason.trim().length > 0;
  const days = valid ? Math.max(1, DateTime.fromMillis(toMs(to), { zone: tz }).startOf("day").diff(DateTime.fromMillis(toMs(from), { zone: tz }).startOf("day"), "days").days + 1) : 1;
  return (
    <Modal title="Close a stand" onClose={onCancel}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) onSubmit({ kind: "stand-closure", stand, from: toMs(from), to: toMs(to), reason: reason.trim() }, day);
        }}
      >
        <label className="block text-[12px] font-semibold">
          Stand
          <select className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={stand} onChange={(e) => setStand(e.target.value)}>
            {config.stands
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-[12px] font-semibold">
            From
            <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="block text-[12px] font-semibold">
            To
            <input type="datetime-local" className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {valid && days > 1 && (
          <div className="text-[12px] text-ink-2">
            {days} days. The recovery options are worked out for {DateTime.fromISO(day).toFormat("ccc d LLL")}, the first day; applying one closes the stand for the whole range, and each later day is re-planned around it when it is opened.
          </div>
        )}
        <label className="block text-[12px] font-semibold">
          Reason
          <input className="mt-1 w-full rounded-md border border-line bg-panel px-2 py-1.5 text-[13px]" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
        </label>
        <div className="flex flex-wrap gap-1">
          {["Fuel spill", "Jet bridge fault", "Maintenance", "FOD inspection", "VIP movement"].map((r) => (
            <button type="button" key={r} onClick={() => setReason(r)} className="rounded-full border border-line px-2 py-0.5 text-[11px] hover:border-ink-3">
              {r}
            </button>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="rounded-lg border border-line px-3 py-2 text-[13px] font-semibold">
            Cancel
          </button>
          <button disabled={!valid} className="rounded-lg bg-ink px-3 py-2 text-[13px] font-bold text-panel disabled:opacity-50">
            Show recovery options
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** A flight a planner took off the board. It is not a plan version, so undo cannot reach it. */
export interface OffBoard {
  id: string;
  label: string;
  note?: string;
}

export function VersionHistory({
  date,
  tz,
  onClose,
  onUndo,
  canEdit,
  offBoard,
  onRestore,
  extended = [],
  onRemoveExtension,
}: {
  date: string;
  tz: string;
  onClose: () => void;
  onUndo: () => void;
  canEdit: boolean;
  offBoard: OffBoard[];
  onRestore: (id: string) => void;
  /**
   * Stands, desks and belts held past their usual end. They are planner actions rather
   * than plan versions, so they outlast re-plans, and they are listed here to be found
   * and taken back.
   */
  extended?: OffBoard[];
  onRemoveExtension?: (id: string) => void;
}) {
  const q = useQuery({ queryKey: ["versions", date], queryFn: () => api.versions(date) });
  return (
    <div className="absolute top-full right-0 z-50 mt-1 w-[26rem] rounded-xl border border-line bg-panel shadow-2xl">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        <History className="size-4 text-ink-2" />
        <div className="flex-1 text-[13px] font-bold">Plan history</div>
        {canEdit && (
          <button onClick={onUndo} className="flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[12px] font-semibold hover:border-ink-3">
            <RotateCcw className="size-3.5" /> Undo last
          </button>
        )}
        <button onClick={onClose} className="rounded p-1 text-ink-3 hover:bg-panel-2" aria-label="Close">
          <X className="size-4" />
        </button>
      </div>
      {offBoard.length > 0 && (
        <div className="border-b border-line px-3 py-2">
          <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Off the board</div>
          {offBoard.map((e) => (
            <div key={e.id} className="flex items-center gap-2 py-1">
              <div className="min-w-0 flex-1 text-[12.5px] leading-snug">
                <span className="font-semibold">{e.label}</span>
                {e.note && <span className="text-ink-3"> · {e.note}</span>}
              </div>
              {canEdit && (
                <button onClick={() => onRestore(e.id)} className="rounded-md border border-line px-2 py-0.5 text-[12px] font-semibold hover:border-ink-3">
                  Restore
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {extended.length > 0 && (
        <div className="border-b border-line px-3 py-2">
          <div className="text-[11px] font-semibold tracking-wide text-ink-3 uppercase">Extended</div>
          {extended.map((e) => (
            <div key={e.id} className="flex items-center gap-2 py-1">
              <div className="min-w-0 flex-1 text-[12.5px] leading-snug">
                <span className="font-semibold">{e.label}</span>
                {e.note && <span className="text-ink-3"> · {e.note}</span>}
              </div>
              {canEdit && onRemoveExtension && (
                <button onClick={() => onRemoveExtension(e.id)} className="rounded-md border border-line px-2 py-0.5 text-[12px] font-semibold hover:border-ink-3">
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="scroll-thin max-h-96 overflow-auto p-1">
        {q.data?.map((v, i) => (
          <div key={v.id} className="flex gap-2 rounded-lg px-2 py-1.5 hover:bg-panel-2">
            <div className="tabular w-10 shrink-0 pt-0.5 text-[11px] text-ink-3">v{v.id}</div>
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] leading-snug">
                {v.summary} {i === 0 && <span className="ml-1 rounded bg-ok/15 px-1 text-[10px] font-bold text-ok">current</span>}
              </div>
              <div className="text-[11px] text-ink-3">
                {DateTime.fromISO(v.createdAt).setZone(tz).toFormat("HH:mm:ss")} · {v.createdBy ?? "system"}
                {v.score && ` · quality ${v.score.quality}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
